// WHO THE APP PRESENTS AS "ME" — the boot-time resolution of `currentUser`.
//
// ── THE DEFECT THIS FIXES (brief 48 §1) ─────────────────────────────────────
//
// `[MEASURED-Laurent, staging, 2026-08-08]` Test Secretary is an assignee of
// *TEST BOND 1* and her Dashboard read **My 0 · All 2**; James Bond is an
// assignee of three events and read **My 0 · All 3**, while the whole-agenda
// filter read **My 1** on a June row. The brief's hypothesis was a MIXED
// ARRAY — strings from one writer, numbers from another, silently missed by
// the readers' exact-type `includes`. It is refuted: every element of every
// `AgendaEvent.assigned` array in the tree is a JSON `number`, in the June
// rows and in the rows created that afternoon alike, and the API hands the
// client the same numbers. The stored data was never the problem.
//
// `currentUser` was. All three sessions were admin "Viewing as" simulations
// (`[simulate] adminId=1 … simulatedAs=10 / simulatedAs=8`, staging API log),
// and in a simulated session this resolution returned **1, the admin**. Every
// number Laurent read reproduces exactly at `currentUser = 1`, including the
// odd one: on James Bond's visible set the only row assigned to the admin is
// `#141 2026-06-13 "proof8 alert-to-admin"` — one row, and a June one. The
// split looked chronological because the admin was an assignee in June and is
// not an assignee on today's rows. It was never about when a row was written.
//
// ── THE CONTRACT ────────────────────────────────────────────────────────────
//
// `currentUser` is the identity the SERVER IS ACTING AS. `authUserId` is the
// real signed-in human and is a different question (App.tsx:1034-1037): it is
// deliberately left as the admin during a simulation so the switcher and the
// admin-only UI survive. The two were being answered from one source.
//
// So the simulated target wins, and it wins because the alternative is
// incoherent rather than merely wrong: the server has already scoped every
// row in the payload to that identity, so any other value makes the client
// disagree with its own data — which is precisely what "My 0" was.
//
// The target is read from the SIGNED TOKEN (`_decodeSimulation()`, the same
// decode that drives the "Viewing as" banner), never from `lx:cu`. A session's
// identity is the thing the server verifies, not a mirror of it that a stale
// write could contradict.
//
// ── WHERE THE ANSWER BELONGS, AND WHY IT IS NOT AT THE COMPARISONS ──────────
//
// Three readers compare against this value today — `Dashboard.tsx:442`, `:802`,
// `:1009` — and `AgendaView.tsx:327`, `:329`, `:1208`, `App.tsx:15953` and
// `CaseView.tsx:927` do the same with the same operator. Coercing at any of
// them would have fixed nothing: both sides were already numbers. There was
// one wrong value and N correct readers, so there is one place to fix it, and
// the fourth reader written next month gets it without knowing this happened.
//
// ── `storedSelection` IS UNREACHABLE, AND THAT IS A SECOND, SEPARATE DEFECT ──
//
// It is kept in its original position, last, and this commit does not change
// what it does — which is nothing. `_readAuthIdentity()` returns non-null
// whenever `lx:authUser` exists, and `login()` (api.ts:282) writes that on
// every sign-in, so the `??` never falls through to it. `lx:cu` therefore has
// four writers and no reader, and the visible consequence is that the Settings
// "I am" selector (SettingsView.tsx:787) reverts on every reload. Making it
// live is a behaviour ruling — does a local profile choice outrank the
// authenticated identity? — and is not smuggled in beside a defect fix.

/** The signed-in identity, as `_readAuthIdentity()` in App.tsx returns it. */
export type AuthIdentity = {
  id: number;
  isAdmin?: boolean;
  source?: string;
} | null;

/** As `_decodeSimulation()` in App.tsx returns it, from the signed JWT. */
export type Simulation = {
  simulatedBy: number;
  simulatedUserId: number;
  expiresAt?: number | null;
} | null;

export type IdentityInputs = {
  /** `_decodeSimulation()` — non-null only while "Viewing as" is active. */
  simulation?: Simulation;
  /** `_readAuthIdentity()` — `lx:authUser`, falling back to the JWT. */
  authIdentity?: AuthIdentity;
  /** `ld(SK.cu)` — the persisted `lx:cu` selection. See the note above. */
  storedSelection?: number | null;
};

export function presentedIdentity({
  simulation,
  authIdentity,
  storedSelection,
}: IdentityInputs): number | null {
  return (
    simulation?.simulatedUserId ?? authIdentity?.id ?? storedSelection ?? null
  );
}
